import React, {useCallback, useEffect, useState} from 'react';
import {ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {QUIKLEARN_BACKEND_URL} from 'config/app.config';
import {DEMO_ACCOUNT_KEY, DEMO_CURRICULUM_ID, DEMO_UNIT_ID, demoSessionId} from 'utils/trialDemo';
import {track} from 'firstVisit/analytics';
import {FONTS, TYPE} from 'style/typography';

/**
 * The Try Quik Demo start (utils/trialDemo.js): signs the visitor in with their own demo account (kept in this
 * browser so a return visit reuses it), asks for a launch into the demo unit the way the portal does, and reloads the
 * player with it. Web only.
 */
const readStoredAccount = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(DEMO_ACCOUNT_KEY) || 'null');
    return stored?.username && stored?.password ? stored : null;
  } catch {
    return null;
  }
};

const signInDemoVisitor = async () => {
  const post = body =>
    fetch(`${QUIKLEARN_BACKEND_URL}/api/auth/anonymous`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    });
  const stored = readStoredAccount();
  let response = stored ? await post({email: stored.username, password: stored.password}).catch(() => null) : null;
  if (!response || !response.ok) {
    try {
      window.localStorage.removeItem(DEMO_ACCOUNT_KEY);
    } catch {
      // nothing stored to forget
    }
    response = await post({});
  }
  if (!response.ok) throw new Error(`Demo sign-in failed (${response.status})`);
  const data = (await response.json())?.data || {};
  if (data.credentials?.username && data.credentials?.password) {
    try {
      window.localStorage.setItem(DEMO_ACCOUNT_KEY, JSON.stringify(data.credentials));
    } catch {
      // private mode: the next visit gets a new demo account
    }
  }
  const accessToken = data.tokens?.AccessToken;
  const userId = data.user?.userId;
  if (!accessToken || !userId) throw new Error('Demo sign-in returned no session');
  return {accessToken, userId};
};

const launchDemo = async () => {
  const {accessToken, userId} = await signInDemoVisitor();
  const response = await fetch(`${QUIKLEARN_BACKEND_URL}/api/auth/token/generate-metaverse-token`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`},
    body: JSON.stringify({userId, curriculumId: DEMO_CURRICULUM_ID, unitId: DEMO_UNIT_ID}),
  });
  if (!response.ok) throw new Error(`Demo launch failed (${response.status})`);
  const body = await response.json();
  const launchToken = body?.token || body?.data?.token;
  if (!launchToken) throw new Error('Demo launch returned no token');
  track('try_quik_demo_started', {demo_session_id: demoSessionId(), unit_id: DEMO_UNIT_ID, course_id: DEMO_CURRICULUM_ID});
  const url = new URL(`${window.location.origin}/`);
  url.searchParams.set('avatarSelection', 'false');
  url.searchParams.set('customCourse', 'true');
  url.searchParams.set('showTrialDemo', 'true');
  url.hash = new URLSearchParams({launch_token: launchToken, access_token: accessToken}).toString();
  window.location.replace(url.toString());
};

export default function TrialDemo() {
  const [failed, setFailed] = useState(false);
  const start = useCallback(() => {
    setFailed(false);
    launchDemo().catch(error => {
      console.error('Try Quik Demo could not start', {message: error?.message || null});
      setFailed(true);
    });
  }, []);
  useEffect(() => {
    if (Platform.OS === 'web') start();
  }, [start]);

  return (
    <View style={styles.screen}>
      {failed ? (
        <>
          <Text style={styles.title}>The demo could not start</Text>
          <TouchableOpacity accessibilityRole="button" onPress={start} style={styles.button}>
            <Text style={styles.buttonText}>Try again</Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <ActivityIndicator size="large" color="#C5E836" />
          <Text style={styles.line}>Starting your demo…</Text>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {color: '#fff', fontFamily: FONTS.heading, fontSize: TYPE.xl, fontWeight: '700', marginBottom: 16},
  line: {color: '#fff', fontFamily: FONTS.body, fontSize: TYPE.base, marginTop: 16},
  button: {backgroundColor: '#C5E836', borderRadius: 12, paddingHorizontal: 24, paddingVertical: 12},
  buttonText: {color: '#000', fontFamily: FONTS.body, fontSize: TYPE.base, fontWeight: '700'},
});
